import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const API_BASE_URL = `${API_URL.replace(/\/$/, '')}/api`;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Logout callback — set by AuthContext to avoid hard window.location redirects
let onForceLogout = null;
export const setForceLogoutHandler = (handler) => { onForceLogout = handler; };

const forceLogout = () => {
  localStorage.removeItem('adminAccessToken');
  localStorage.removeItem('adminRefreshToken');
  localStorage.removeItem('adminUser');
  if (onForceLogout) {
    onForceLogout();
  } else {
    window.location.href = '/login';
  }
};

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminAccessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401 with silent token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't retry refresh requests or already-retried requests
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      if (isRefreshing) {
        // Queue requests while a refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('adminRefreshToken');
      if (refreshToken) {
        try {
          const res = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
          const newAccessToken = res.data.accessToken;
          localStorage.setItem('adminAccessToken', newAccessToken);
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          processQueue(null, newAccessToken);
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          forceLogout();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // No refresh token available
      forceLogout();
    }

    if (error.response?.status === 403) {
      forceLogout();
    }

    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  adminLogin: (email, password, deviceToken) =>
    api.post('/auth/admin/login', { email, password, deviceToken }),
  verifyTOTP: (tempToken, code, rememberDevice) =>
    api.post('/auth/admin/verify-totp', { tempToken, code, rememberDevice }),
};

// Admin TOTP management
export const adminTOTPAPI = {
  getStatus: () => api.get('/admin/totp/status'),
  setup: () => api.post('/admin/totp/setup'),
  enable: (code) => api.post('/admin/totp/enable', { code }),
  disable: (password, code) => api.post('/admin/totp/disable', { password, code }),
};

// Admin endpoints
export const adminAPI = {
  // Stats
  getStats: () => api.get('/admin/stats'),

  // Users
  searchUsers: (query) => api.get('/admin/users/search', { params: { q: query } }),
  listUsers: (params) => api.get('/admin/users', { params }),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  updateUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  createUser: (data) => api.post('/admin/users', data),

  // Password
  sendPasswordReset: (userId) => api.post(`/admin/users/${userId}/send-password-reset`),
  sendGuideToUser: (userId) => api.post(`/admin/users/${userId}/send-guide`),
  sendVCardToUser: (userId) => api.post(`/admin/users/${userId}/send-vcard`),
  setTemporaryPassword: (userId, password, notify) => 
    api.post(`/admin/users/${userId}/set-password`, { password, notify }),

  // Subscription
  getSubscription: (userId) => api.get(`/admin/users/${userId}/subscription`),
  updateQuantity: (userId, quantity, reason) => 
    api.post(`/admin/users/${userId}/subscription/quantity`, { quantity, reason }),
  cancelSubscription: (userId, immediately, reason) => 
    api.post(`/admin/users/${userId}/subscription/cancel`, { immediately, reason }),
  issueRefund: (userId, amount, reason) =>
    api.post(`/admin/users/${userId}/refund`, { amount, reason }),
  updateTrialEnd: (userId, trialEndDate) =>
    api.post(`/admin/users/${userId}/subscription/trial`, { trial_end_date: trialEndDate }),

  // Billing & Loyalty
  getBillingHistory: (userId) => api.get(`/admin/users/${userId}/billing-history`),
  getLoyaltyStats: (userId) => api.get(`/admin/users/${userId}/loyalty-stats`),

  // Client management
  manuallyVerify: (userId, clientId, reason) =>
    api.post(`/admin/users/${userId}/clients/${clientId}/verify`, { reason }),
  transferClient: (clientId, fromUserId, toUserId, reason) =>
    api.post(`/admin/clients/${clientId}/transfer`, { fromUserId, toUserId, reason }),
  linkClient: (userId, clientId) =>
    api.post(`/admin/users/${userId}/clients/${clientId}/link`),
  unlinkClient: (userId, clientId) =>
    api.delete(`/admin/users/${userId}/clients/${clientId}`),
  sendGuideToClient: (userId, clientId) =>
    api.post(`/admin/users/${userId}/clients/${clientId}/send-guide`),
  createAndLinkClient: (userId, clientData) =>
    api.post(`/admin/users/${userId}/clients`, clientData),

  // Account actions
  deleteUser: (userId, reason) =>
    api.delete(`/admin/users/${userId}`, { data: { reason } }),

  // Notes
  addNote: (userId, note) => api.post(`/admin/users/${userId}/notes`, { note }),

  // Audit Log
  getAuditLog: (params) => api.get('/admin/audit-log', { params }),

  // Export
  exportUserData: (userId) => api.get(`/admin/users/${userId}/export`),

  // Activity
  getUserActivity: (userId, params) => api.get(`/admin/users/${userId}/activity`, { params }),
  getGlobalActivity: (params) => api.get('/admin/activity', { params }),

  // Phone Reputation
  getPhoneReputationStats: () => api.get('/admin/phone-reputation/stats'),
  getPhoneReputationList: (params) => api.get('/admin/phone-reputation', { params }),
  getPhoneReputation: (phoneNumber) => api.get(`/admin/phone-reputation/${encodeURIComponent(phoneNumber)}`),
  updatePhoneReputation: (phoneNumber, data) => api.patch(`/admin/phone-reputation/${encodeURIComponent(phoneNumber)}`, data),
  deletePhoneReputation: (phoneNumber) => api.delete(`/admin/phone-reputation/${encodeURIComponent(phoneNumber)}`),
  importPhoneReputation: (numbers) => api.post('/admin/phone-reputation/import', { numbers }),

  // Access Override
  getAccessOverride: (userId) => api.get(`/admin/users/${userId}/access-override`),
  updateAccessOverride: (userId, data) => api.put(`/admin/users/${userId}/access-override`, data),

  // Promo Codes
  getPromoCodeStats: () => api.get('/admin/promo-codes/stats'),
  listPromoCodes: (params) => api.get('/admin/promo-codes', { params }),
  createPromoCode: (data) => api.post('/admin/promo-codes', data),
  deactivatePromoCode: (promoCodeId, reason) => api.post(`/admin/promo-codes/${promoCodeId}/deactivate`, { reason }),
  reactivatePromoCode: (promoCodeId) => api.post(`/admin/promo-codes/${promoCodeId}/reactivate`),
  updatePromoCode: (promoCodeId, data) => api.patch(`/admin/promo-codes/${promoCodeId}`, data),
  deletePromoCode: (promoCodeId) => api.delete(`/admin/promo-codes/${promoCodeId}`),
  getPromoCodeRedemptions: (promoCodeId) => api.get(`/admin/promo-codes/${promoCodeId}/redemptions`),
  listCoupons: () => api.get('/admin/coupons'),

  // Feature Flags
  getFeatureFlags: () => api.get('/admin/feature-flags'),
  updateFeatureFlag: (key, data) => api.put(`/admin/feature-flags/${key}`, data),
  pauseAllIngestion: (data) => api.post('/admin/feature-flags/pause-all', data),
  resumeAllIngestion: () => api.post('/admin/feature-flags/resume-all'),
  getUserIngestion: (userId) => api.get(`/admin/users/${userId}/ingestion`),
  updateUserIngestion: (userId, data) => api.put(`/admin/users/${userId}/ingestion`, data),

  // Waitlist
  getWaitlistStats: () => api.get('/admin/waitlist/stats'),
  getWaitlist: (params) => api.get('/admin/waitlist', { params }),
  deleteWaitlistEntry: (id) => api.delete(`/admin/waitlist/${id}`),
  notifyWaitlist: (data) => api.post('/admin/waitlist/notify', data),
  
  // Observability & Analytics
  getLogs: (params) => api.get('/admin/logs', { params }),
  getMetrics: (params) => api.get('/admin/metrics', { params }),
  cleanupLogs: (daysToKeep) => api.post('/admin/logs/cleanup', { daysToKeep }),
  getFeedbackAnalytics: (params) => api.get('/admin/analytics/feedback', { params }),

  // FTC Data
  getFTCStats: () => api.get('/admin/ftc/stats'),
  parseCSVHeaders: (csvData) => api.post('/admin/ftc/parse-headers', { csvData }),
  importFTCData: (csvData, sourceFile, columnMapping) => api.post('/admin/ftc/import', { csvData, sourceFile, columnMapping }),
  getFTCImportStatus: (jobId) => api.get(`/admin/ftc/import/${jobId}`),
  getFTCImportHistory: (params) => api.get('/admin/ftc/history', { params }),
  searchFTCNumbers: (params) => api.get('/admin/ftc/search', { params }),
  getFTCCategories: () => api.get('/admin/ftc/categories'),
  testFTCApi: () => api.get('/admin/ftc/api/test'),
  triggerFTCSync: (params) => api.post('/admin/ftc/api/sync', params),
  cancelFTCSync: () => api.post('/admin/ftc/api/sync/cancel'),
  getSyncProgress: () => api.get('/admin/ftc/sync/status'),

  // Sender Verification
  getSenderStats: (params) => api.get('/admin/senders/stats', { params }),
  getSenderCategories: () => api.get('/admin/senders/categories'),
  getKnownSenders: (params) => api.get('/admin/senders/known', { params }),
  createKnownSender: (data) => api.post('/admin/senders/known', data),
  deleteKnownSender: (id) => api.delete(`/admin/senders/known/${id}`),
  getSuspiciousSenders: (params) => api.get('/admin/senders/suspicious', { params }),
  createSuspiciousSender: (data) => api.post('/admin/senders/suspicious', data),
  deleteSuspiciousSender: (id) => api.delete(`/admin/senders/suspicious/${id}`),
  testVerifySender: (data) => api.post('/admin/senders/verify', data),
  getRecentVerifications: (params) => api.get('/admin/senders/verifications', { params }),

  // Financials / Cost Tracking
  getFinancialsOverview: (params) => api.get('/admin/financials/overview', { params }),
  getCostBreakdown: (params) => api.get('/admin/financials/costs/breakdown', { params }),
  getDailyCosts: (params) => api.get('/admin/financials/costs/daily', { params }),
  getCostsByUser: (params) => api.get('/admin/financials/costs/by-user', { params }),
  getUserCostDetails: (userId, params) => api.get(`/admin/financials/costs/user/${userId}`, { params }),
  getStaticCosts: () => api.get('/admin/financials/static-costs'),
  createStaticCost: (data) => api.post('/admin/financials/static-costs', data),
  updateStaticCost: (id, data) => api.put(`/admin/financials/static-costs/${id}`, data),
  deleteStaticCost: (id) => api.delete(`/admin/financials/static-costs/${id}`),
  getRevenueDetails: (params) => api.get('/admin/financials/revenue', { params }),

  // Cancellation Analytics
  getCancellationAnalytics: (params) => api.get('/admin/cancellation-analytics', { params }),
  getCancellationFeedback: (params) => api.get('/admin/cancellation-feedback', { params }),

  // General Feedback
  getNewFeedbackCount: () => api.get('/admin/feedback/new-count'),
  getGeneralFeedback: (params) => api.get('/admin/feedback', { params }),
  updateGeneralFeedback: (id, data) => api.patch(`/admin/feedback/${id}`, data),
  getAdminFeedbackAttachmentUrl: (feedbackId, attachmentKey) =>
    api.post('/admin/feedback/attachment-url', { feedbackId, attachmentKey }),
  uploadFeedbackAttachments: (feedbackId, formData) =>
    api.post(`/admin/feedback/${feedbackId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Safelist (on behalf of users)
  getUserSafelist: (userId, params) => api.get(`/admin/users/${userId}/safelist`, { params }),
  addUserSafelistEntry: (userId, data) => api.post(`/admin/users/${userId}/safelist`, data),
  updateUserSafelistEntry: (userId, entryId, data) =>
    api.put(`/admin/users/${userId}/safelist/${entryId}`, data),
  deleteUserSafelistEntry: (userId, entryId) =>
    api.delete(`/admin/users/${userId}/safelist/${entryId}`),

  // Daily Summary
  getDailySummaryConfig: () => api.get('/admin/daily-summary/config'),
  updateDailySummaryConfig: (data) => api.put('/admin/daily-summary/config', data),
  testDailySummary: () => api.post('/admin/daily-summary/test'),

  // Support Line
  getSupportLineSettings: () => api.get('/admin/support-line/settings'),
  updateSupportLineSettings: (data) => api.put('/admin/support-line/settings', data),
  testSupportLineSettings: () => api.post('/admin/support-line/test'),

  // Voicemails
  getVoicemailUnreadCount: () => api.get('/admin/voicemails/unread-count'),
  getVoicemails: (params) => api.get('/admin/voicemails', { params }),
  getVoicemail: (id) => api.get(`/admin/voicemails/${id}`),
  getVoicemailAudio: (id) => api.get(`/admin/voicemails/${id}/audio`),
  markVoicemailRead: (id, isRead) => api.patch(`/admin/voicemails/${id}/read`, { is_read: isRead }),
  deleteVoicemail: (id) => api.delete(`/admin/voicemails/${id}`),
  initiateReturnCall: (id, adminPhone) => api.post(`/admin/voicemails/${id}/return-call`, { admin_phone: adminPhone }),

  // Call Logs
  getCallLogUnreadCount: () => api.get('/admin/call-logs/unread-count'),
  getCallLogs: (params) => api.get('/admin/call-logs', { params }),
  markCallLogRead: (id, isRead) => api.patch(`/admin/call-logs/${id}/read`, { is_read: isRead }),

  // Release Notes
  listReleases: () => api.get('/admin/releases'),
  createRelease: (data) => api.post('/admin/releases', data),
  updateRelease: (id, data) => api.patch(`/admin/releases/${id}`, data),
  deleteRelease: (id) => api.delete(`/admin/releases/${id}`),

  // Campaign Links
  listCampaignLinks: (params) => api.get('/admin/campaign-links', { params }),
  createCampaignLink: (data) => api.post('/admin/campaign-links', data),
  createBulkCampaignLinks: (data) => api.post('/admin/campaign-links/bulk', data),
  deleteCampaignLink: (id) => api.delete(`/admin/campaign-links/${id}`),

  // Event/Message Status (uses reporting endpoints with admin auth)
  updateEventStatus: (eventId, data) => api.patch(`/reporting/message/${eventId}`, data),
  acknowledgeMoneySent: (eventId) => api.patch(`/reporting/message/${eventId}/acknowledge-money-sent`),
  acknowledgePiiDisclosed: (eventId) => api.patch(`/reporting/message/${eventId}/acknowledge-pii-disclosed`),

  // Training Management
  getTrainingStats: () => api.get('/admin/training/stats'),
  getTrainingScenarios: () => api.get('/admin/training/scenarios'),
  getTrainingUsers: (search) => api.get('/admin/training/users', { params: { search } }),
  getTrainingSessions: (params) => api.get('/admin/training/sessions', { params }),
  createTrainingSession: (data) => api.post('/admin/training/sessions', data),
  sendTrainingSession: (sessionId) => api.post(`/admin/training/sessions/${sessionId}/send`),
  cancelTrainingSession: (sessionId) => api.delete(`/admin/training/sessions/${sessionId}`),

  // Scheduled Training
  getScheduledTrainingSettings: (userId) => api.get(`/admin/users/${userId}/training-settings`),
  updateScheduledTrainingSettings: (userId, data) => api.put(`/admin/users/${userId}/training-settings`, data),
  getScheduledTrainingSchedule: (userId) => api.get(`/admin/users/${userId}/training-schedule`),
  getScheduledTrainingSendHistory: (userId, limit) => api.get(`/admin/users/${userId}/training-send-history`, { params: { limit } }),
  sendScheduledTrainingNow: (userId) => api.post(`/admin/users/${userId}/training-send-now`),
};

export default api;